import React, { useEffect, useState } from 'react'
import { useMutation } from '@apollo/client'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import { toastSweetAlert } from '../../../Helpers/ToastSweetAlert'
import Box from '../../../Global/Box'
import InputController from '../../../Global/InputController'
import Image from './Image'
import { GET_ALL_CONFIG_APP_PAGES } from '../../../../graphql/ConfigApp/configAppPages'
import {
  CREATE_OR_UPDATE_ELEMENT_BannerImageCarousel,
  GET_ALL_ELEMENTS_OF_CONFIG_APP_PAGE,
} from '../../../../graphql/ConfigApp/configAppPageElements'

const refetchConfigsAppPage = {
  query: GET_ALL_CONFIG_APP_PAGES,
  variables: {
    limit: 10,
    offset: 0,
    searchQuery: null,
  },
}

const extensions_allowed_image = ['.png', '.jpg', '.jpeg']
const sizeMaxFile = 8 // EXPRESADO EN MB.

const BannerImageCarousel = ({ pageId, dataElement, setDataElement }) => {
  //SPACE
  const [items, setItems] = useState([])
  const [files, setFiles] = useState([])

  //MUTATIONS:
  const [createOrUpdate] = useMutation(
    CREATE_OR_UPDATE_ELEMENT_BannerImageCarousel
  )

  //FORM:
  const validationSchema = Yup.object().shape({
    height: Yup.string().required('Campo requerido'),
  })

  const {
    methods,
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm({
    resolver: yupResolver(validationSchema),
  })

  const handleFiles = async ({ e, index }) => {
    if (!e.target.files[0]) return

    const fileName = e.target.files[0].name.toLowerCase()
    const lastDot = fileName.lastIndexOf('.')
    const extension = fileName.slice(lastDot, fileName.length)

    if (extensions_allowed_image.indexOf(extension) === -1) {
      //setFile(null)
      return toastSweetAlert({
        mode: 'error',
        message: 'Solo se admite JPG, JPGE y PNG',
      })
    }

    if (e.target.files[0].size / 1048576 > sizeMaxFile) {
      //setFile(null)
      return toastSweetAlert({
        mode: 'error',
        message: `El archivo no puede superar los ${sizeMaxFile}MB`,
      })
    }

    setFiles((old) => {
      if (old.index === index) {
        return old.map((item) => {
          if (item.index === index) {
            return { index, file: e.target.files[0] }
          }
          return item
        })
      } else {
        return [...old, { index, file: e.target.files[0] }]
      }
    })
  }

  const handleCancel = () => setDataElement(null)

  const handleAddElement = () => setItems((old) => [...old, {}])

  const handleDeleteItem = ({ index }) => {
    const ajustItems = items.map((item, idx) => {
      if (idx === index) return { ...item, hide: true }
      return item
    })
    setItems(() => [...ajustItems])
  }

  const onSubmit = async (dataForm) => {
    try {
      let indexOk = []

      if (items.length === 0) throw new Error('Se debe enviar minimo un item')

      items.map((item, index) => {
        if (item.hide === true) return false
        return indexOk.push(index)
      })

      if (indexOk.length === 0) throw new Error('Se debe enviar minimo un item')

      const itemsSend = []

      for await (const [index, item] of dataForm?.image.entries()) {
        if (indexOk.includes(index)) {
          const foundItems = items.find((f, idx) => idx === index)
          const existFile = files.find((f) => f.index === index)

          if (foundItems) {
            let file = null

            if (existFile?.file) {
              file = existFile.file
            }

            itemsSend.push({
              link: item?.link,
              file,
              urlKey: foundItems.urlKey,
            })
          } else {
            if (!existFile?.file) {
              throw new Error('Hay elementos que necesitan una imagen')
            }

            itemsSend.push({
              link: item?.link,
              file: existFile.file,
              urlKey: null,
            })
          }
        }
      }

      if (itemsSend.length === 0)
        throw new Error('Se debe enviar minimo un item')

      let message = ''

      if (dataElement?.id) {
        //Es actualizacion
        const variables = {
          inputGeneral: {
            id: dataElement.id,
            config_app_page_id: dataElement.config_app_page_id,
            order: dataElement.order,
            layout: dataElement.layout,
          },
          inputElement: {
            height: parseInt(dataForm.height),
            items: itemsSend,
          },
        }
        await createOrUpdate({
          variables,
          refetchQueries: [
            {
              query: GET_ALL_ELEMENTS_OF_CONFIG_APP_PAGE,
              variables: { configAppPageId: pageId },
            },
          ],
        })
        message = 'Se actualizo correctamente'
      } else {
        //Es nuevo
        const variables = {
          inputGeneral: {
            id: null,
            config_app_page_id: dataElement.config_app_page_id,
            order: null,
            layout: dataElement.layout,
          },
          inputElement: {
            height: parseInt(dataForm.height),
            items: itemsSend,
          },
        }
        await createOrUpdate({
          variables,
          refetchQueries: [
            { ...refetchConfigsAppPage },
            {
              query: GET_ALL_ELEMENTS_OF_CONFIG_APP_PAGE,
              variables: { configAppPageId: pageId },
            },
          ],
        })
        message = 'Se creo con exito'
      }

      setDataElement(null)
      setItems([])

      return toastSweetAlert({ mode: 'ok', message })
    } catch (error) {
      return toastSweetAlert({
        mode: 'error',
        message: error.message,
      })
    }
  }

  useEffect(() => {
    if (dataElement?.id) {
      const data = dataElement
      const dataItems = data?.itemMain?.items

      let dataFormated = {}

      dataFormated.height = data?.itemMain?.height

      if (dataItems.length > 0) {
        dataItems.map((item, index) => {
          dataFormated = {
            ...dataFormated,
            [`image[${index}].link`]: item?.link,
          }
          return true
        })
      }

      reset(dataFormated)
      setItems(dataElement?.itemMain?.items)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataElement])

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            withCard={false}
            title="Elemento"
            btnSubmit={items && items.length > 0 ? true : false}
            btnSubmitText={`${dataElement?.id ? 'Actualizar' : 'Crear'}`}
            btnSecondFunctionTitle="Cancelar"
            btnSecondFunction={() => handleCancel()}
            errors={errors}
            content={
              <>
                <div className="row">
                  <div className="col-12 col-sm-4 mb-3">
                    <InputController
                      label="Alto"
                      name="height"
                      inputType="number"
                      placeholderTxt="Escribe height"
                      control={control}
                    />
                  </div>
                </div>

                <div className="row mb-4">
                  <div className="col-12">
                    <button
                      className="btn btn-migrates"
                      type="button"
                      style={{ float: 'right' }}
                      onClick={() => handleAddElement()}
                    >
                      <i className="fas fa-plus" />
                      &nbsp;Agregar item
                    </button>
                  </div>
                </div>

                {items &&
                  items.length > 0 &&
                  items.map((item, index) => {
                    return (
                      <div
                        className="mb-4"
                        key={index}
                        style={{ display: item?.hide === true ? 'none' : '' }}
                      >
                        <Image
                          index={index}
                          item={item}
                          control={control}
                          handleDeleteItem={handleDeleteItem}
                          handleFiles={handleFiles}
                        />
                      </div>
                    )
                  })}
              </>
            }
          />
        </form>
      </FormProvider>
    </>
  )
}

export default BannerImageCarousel
